import React, { Component, useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { Box, CssBaseline, Typography, useMediaQuery } from "@mui/material";
import withTheme from "@mui/styles/withTheme";
import {
  ThemeProvider,
  StyledEngineProvider,
  useTheme
} from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { fetchUnreadMessages } from "../../redux/actions/messagesActions";
import { fetchResources } from "../../redux/actions/learningCenter/resourcesActions";
import {
  fetchSupportInfo,
  changeSupportModalOpenState
} from "../../redux/actions/supportActions";
import {
  root,
  dashboard,
  administration,
  content,
  policies,
  communications,
  resources,
  announcements,
  announcementsList,
  announcementsCreate,
  calendar,
  messages,
  profile,
  settings,
  validate,
  homeworkSubmission,
  legacySubmission,
  resourceViewer,
  appPrefix as adminApp
} from "../../routes/AdminRoutes";
import AdminTopNav from "./AdminTopNav";
import visionLogoImage from "../../assets/images/vision_logo.jpeg";
import ThemeWrapper from "../../theme/ThemeWrapper";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ModalContainer from "../../components/ModalContainer/ModalContainer";
import PolicyPartnerLoginFrame from "../../components/PolicyPartnerLoginFrame/PolicyPartnerLoginFrame";
import { withTranslation } from "react-i18next";
import SupportModal from "../../components/SupportModal";
import { Route, Redirect } from "react-router-dom";
import AdminDashboard from "./AdminDashboard";
import AdminAdministration from "./AdminAdministration";
import AdminContent from "./AdminContent";
import AdminPolicies from "./AdminPolicies";
import AdminCommunications from "./AdminCommunications";
import AdminSideNav from "./AdminSideNav";
import Resources from "../Resources/Resources";
import AnnouncementsList from "../../views/Announcements/AnnouncementsList";
import AnnouncementsNew from "../../views/Announcements/AnnouncementsNew";
import Calendar from "../../containers/LearningCenter/LearningCenterCalendar";
import createAdminTheme from "../../theme/adminTheme";
import AdminMessages from "./AdminMessages";
import AdminValidate from "./AdminValidate";
import LearningCenterProfile from "../LearningCenter/LearningCenterProfile";
import { learningCenterRoot } from "../../routes/Routes";
import { SnackbarProvider } from "notistack";
import SnackbarContainer from "../Snackbar/SnackbarContainer";
import AdminHomeworkSubmission from "./AdminHomeworkSubmission";
import AdminLegacySubmission from "./AdminLegacySubmission";
import AdminSettings from "./AdminSettings";
import usePermissions from "../../redux/selectors/usePermissions";
import {
  hasRepositoryAccess,
  showFeatureSettings,
  hasPolicyPartnerAdminAccess,
  hasTopLevelRepositoryNav
} from "../../utils/permission_authorization";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import ResourceViewerContainer from "../../components/ResourceViewer/index";
import { makeGetEntities } from "../../redux/selectors/selectors";
import {
  useParentRouteChangeListener,
  usePostMessageOnRouteChange
} from "../../hooks/utils/iframeUtils";
import { isIframed } from "../../utils/iframe";

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
const withWidth = () => WrappedComponent => props => (
  <WrappedComponent {...props} width="xs" />
);

const selector = makeGetEntities();

const useStyles = makeStyles(theme => ({
  content: {
    display: "flex",
    flexDirection: "column",
    marginTop: isIframed ? 0 : theme.sizes.topNavBarHeight,
    float: "right",
    backgroundColor: theme.palette.body,
    minHeight: `calc(100vh - ${theme.sizes.topNavBarHeight}px)`,
    justifyContent: "space-between",
    transition: "width",
    transitionDelay: "225ms"
  }
}));

const getRootRedirectRoute = (
  allowAdminDashboard,
  isAdminOrInstructor,
  isValidateOnly
) => {
  if (allowAdminDashboard) {
    return dashboard;
  } else if (isAdminOrInstructor) {
    return content;
  } else if (isValidateOnly) {
    return validate;
  }
};

const AdminRoutes = () => {
  const {
    isAdmin,
    allowAdminDashboard,
    isAdminOrInstructor,
    isPolicyAdmin,
    isValidateOnly,
    allowEmailAlerts
  } = usePermissions();
  const rootRedirectRoute = getRootRedirectRoute(
    allowAdminDashboard,
    isAdminOrInstructor,
    isValidateOnly
  );

  usePostMessageOnRouteChange();
  useParentRouteChangeListener();
  return (
    <>
      <Route
        exact
        path={root}
        render={() => <Redirect to={rootRedirectRoute} />}
      />
      <Route path={dashboard} component={AdminDashboard} />
      <Route path={administration} component={AdminAdministration} />
      <Route path={content} component={AdminContent} />
      <Route path={policies} component={AdminPolicies} />
      {allowEmailAlerts && (
        <Route path={communications} component={AdminCommunications} />
      )}
      <Route
        path={resourceViewer}
        render={props => (
          <ResourceViewerContainer
            LayoutComponent={AdminPageLayout}
            {...props}
          />
        )}
      />
      <Route
        path={resources}
        render={props => (
          <Resources
            LayoutComponent={AdminPageLayout}
            currentApp={adminApp}
            {...props}
          />
        )}
      />
      <Route
        exact
        path={announcements}
        render={() => <Redirect to={announcementsList} />}
      />
      <Route path={announcementsList} exact component={AnnouncementsList} />
      <Route exact path={announcementsCreate} component={AnnouncementsNew} />
      <Route path={calendar} component={Calendar} />
      <Route path={messages} component={AdminMessages} />
      <Route path={profile} component={LearningCenterProfile} />
      <Route path={validate} component={AdminValidate} />
      {(isAdmin || isPolicyAdmin) && (
        <Route path={settings} component={AdminSettings} />
      )}
      <Route
        path={homeworkSubmission}
        render={({
          match: {
            params: { homeworkSubmissionId }
          }
        }) => (
          <AdminHomeworkSubmission
            homeworkSubmissionId={homeworkSubmissionId}
          />
        )}
      />
      <Route
        path={legacySubmission}
        render={({
          match: {
            params: { legacySubmissionId }
          }
        }) => <AdminLegacySubmission legacySubmissionId={legacySubmissionId} />}
      />
    </>
  );
};

const AdminBodyContent = ({ sideNavExpanded, windowWidth }) => {
  const classes = useStyles();
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const {
    sizes: { sideNavBarWidth, sideNavBarWidthCondensed }
  } = theme;

  /**
   *  Returns the width of the dashboard. Will return 100% for XS screen sizes
   *  or 100% minus the width of the side nav for large screens
   */
  const width =
    useMediaQuery(theme.breakpoints.up("sm")) && !isIframed
      ? `calc(100% - ${
          sideNavExpanded ? sideNavBarWidth : sideNavBarWidthCondensed
        }px)`
      : "100%";

  const [bodyWidth, setBodyWidth] = useState(width);

  // Sets the width of the dashboard based on whether the side nav is open
  useEffect(() => {
    setBodyWidth(width);
  }, [sideNavExpanded, windowWidth]);

  return (
    <Box className={classes.content} style={{ width: bodyWidth }}>
      <AdminRoutes />
      {!isIframed && (
        <Box mt={3} mb={2} mx={3}>
          <Typography variant="body2" color="textSecondary">
            Powered by <b>Vision</b>, a Grace Hill, Inc. product. ©{" "}
            {currentYear} Grace Hill, Inc. All Rights Reserved.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

class Admin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sideNavExpanded: true
    };
  }

  componentDidMount() {
    this.props.fetchUnreadMessages();
    this.props.fetchResources();
  }

  componentDidUpdate() {
    const { i18n, user } = this.props;
    if (user && user.settings.preferred_language !== i18n.language) {
      i18n.changeLanguage(user.settings.preferred_language);
    }
  }

  handleSupportModalClose = () => {
    this.props.changeSupportModalOpenState(false);
  };

  handleSupportClick = () => {
    this.props.fetchSupportInfo();
    this.props.changeSupportModalOpenState(true);
  };

  handleExpandSideNav = expanded => {
    this.setState({ sideNavExpanded: expanded });
  };

  render() {
    const {
      match,
      location: { pathname },
      loading,
      customer,
      user,
      supportModalOpen,
      theme,
      customColor,
      secondaryColor,
      width,
      adminConsoleLogoImage
    } = this.props;
    let resources = this.props.resources.resourceSections;
    const {
      isAdmin,
      isPolicyAdmin,
      allowValidate,
      allowAdminConsole,
      validateEnableMysteryShopReview,
      isEmployeeManager,
      allowValidateRole,
      allowAdminExperience,
      isGoldOrPlatinum
    } = user.permissions;
    const adminTheme = createAdminTheme(theme, customColor, secondaryColor);
    const allowContentToolsForValidate =
      allowValidate &&
      ((validateEnableMysteryShopReview && isEmployeeManager) ||
        allowValidateRole);
    if ((!user.id || !customer.id || !resources) && loading) {
      return (
        <Box
          display="flex"
          minHeight="100vh"
          alignItems="center"
          justifyContent="center"
        >
          <LoadingSpinner visible={this.props.loading} />
        </Box>
      );
    } else {
      if (
        !allowAdminConsole &&
        !(allowContentToolsForValidate || allowAdminExperience)
      ) {
        return <Redirect to={learningCenterRoot} />;
      }

      return (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={adminTheme}>
            <CssBaseline />
            <ThemeWrapper>
              <ModalContainer>
                <SnackbarProvider>
                  <AdminTopNav
                    handleMenuToggle={this.handleMenuToggle}
                    logo={
                      adminConsoleLogoImage
                        ? adminConsoleLogoImage
                        : visionLogoImage
                    }
                    logoStyle="logo"
                    currentPath={match.path || pathname}
                    unreadMessages={this.props.unreadMessages}
                    currentUser={user}
                    onSupportClick={this.handleSupportClick}
                    onExpandSideNav={this.handleExpandSideNav}
                    expanded={this.state.sideNavExpanded}
                    isInstructor={user.permissions.isInstructor && !isAdmin}
                    permissions={user.permissions}
                  />
                  <Box
                    position="absolute"
                    width="100%"
                    height="auto"
                    bgcolor={adminTheme.palette.body}
                  >
                    <AdminSideNav
                      adminLogoImage={
                        adminConsoleLogoImage
                          ? adminConsoleLogoImage
                          : visionLogoImage
                      }
                      currentPath={pathname}
                      onSupportClick={this.handleSupportClick}
                      onExpandSideNav={this.handleExpandSideNav}
                      expanded={this.state.sideNavExpanded}
                      isInstructor={user.permissions.isInstructor}
                      isAdmin={isAdmin}
                      isPolicyAdmin={isPolicyAdmin}
                      hasRepositoryAccess={hasRepositoryAccess(
                        user.permissions
                      )}
                      hasTopLevelRepositoryNav={hasTopLevelRepositoryNav(
                        user.permissions
                      )}
                      hasAdminDashboardAccess={
                        user.permissions.allowAdminDashboard
                      }
                      hasAdministrationAccess={
                        user.permissions.allowAdministration
                      }
                      hasValidateAccess={
                        user.permissions.allowValidate &&
                        (user.permissions.allowValidateRole ||
                          isAdmin ||
                          (user.permissions.validateEnableMysteryShopReview &&
                            isEmployeeManager))
                      }
                      hasPolicyPartnerAdminAccess={hasPolicyPartnerAdminAccess(
                        user.permissions
                      )}
                      hasRepositoryIntegration={
                        user.permissions.allowRepositoryIntegration
                      }
                      hasBulkAssignmentToolAccess={
                        isGoldOrPlatinum && (isAdmin || isPolicyAdmin)
                      }
                      hasSpecialAssignmentsAccess={
                        isGoldOrPlatinum && (isAdmin || isPolicyAdmin)
                      }
                      hasEmailAlertsAccess={user.permissions.allowEmailAlerts}
                      hasFeatureSettings={
                        showFeatureSettings(user.permissions) && isAdmin
                      }
                    />
                    <AdminBodyContent
                      sideNavExpanded={this.state.sideNavExpanded}
                      windowWidth={width}
                    />
                    <SupportModal
                      open={supportModalOpen}
                      onClose={this.handleSupportModalClose}
                    />
                    <SnackbarContainer
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center"
                      }}
                    />
                  </Box>
                </SnackbarProvider>
              </ModalContainer>
            </ThemeWrapper>
            <PolicyPartnerLoginFrame user={user} />
          </ThemeProvider>
        </StyledEngineProvider>
      );
    }
  }
}

const mapStateToProps = state => ({
  adminConsoleLogoImage:
    state.learningCenterSettingsState.adminConsoleLogoImage,
  logoImage: state.learningCenterSettingsState.logoImage,
  learningCenterCaption:
    state.learningCenterSettingsState.learningCenterCaption,
  logoStyle: state.learningCenterSettingsState.logoStyle,
  customColor: state.learningCenterSettingsState.customColor,
  secondaryColor: state.learningCenterSettingsState.secondaryColor,
  unreadMessages: state.messagesState.unreadMessages,
  user: state.userState.user,
  todoList: selector(state.todoState, {
    type: "toDo"
  }),
  customer: state.customerState.customer,
  loading:
    state.userState.loading ||
    state.resourcesState.loading ||
    state.customerState.loading,
  resources: state.resourcesState,
  supportModalOpen: state.supportState.supportInfo.open,
  error: state.learningCenterSettingsState.error
});

const TranslatedAdminConsole = withTranslation()(Admin);

export default compose(
  connect(mapStateToProps, {
    fetchUnreadMessages,
    fetchResources,
    fetchSupportInfo,
    changeSupportModalOpenState
  }),
  withWidth()
)(withTheme(TranslatedAdminConsole));
